import { parseYaml } from './deterministicYaml.js';
import { SCHEMA_VERSION } from './schema.js';

function normalizeVariable(v) {
  if (!v || typeof v !== 'object') return { key: '', value: '', enabled: true };
  return {
    key: v.key ?? '',
    value: v.value ?? '',
    enabled: v.enabled !== false,
    ...(v.secret ? { secret: true } : {}),
    ...(v.scriptAccess ? { scriptAccess: true } : {}),
    ...(Array.isArray(v.options) && v.options.length > 0 ? { options: v.options } : {}),
  };
}

/** Explicit sort_order first; ties keep the order they were read in (the collection index, the position written for environments / collections). */
function bySortOrder(a, b) {
  return (a.sort_order ?? 0) - (b.sort_order ?? 0);
}

/**
 * Fold an untracked `*.local.yaml` sidecar's values back onto the tracked
 * variable list (COL-06). The tracked file carries `{key, enabled, secret}`
 * with no `value`; the sidecar carries `{key, value}`. Merging here — rather
 * than in the filesystem loader — is what keeps
 * `deserialize(serialize(ws)) === ws` a property of this module rather than of
 * whichever caller happened to read the files.
 *
 * A secret with no sidecar entry restores as an empty value, which is the
 * correct outcome for the case this whole split exists for: someone else
 * cloned the repo and does not have your keys.
 */
function mergeSecretValues(vars, sidecar) {
  const bySidecarKey = new Map(
    (Array.isArray(sidecar?.variables) ? sidecar.variables : [])
      .filter((v) => v && v.key != null)
      .map((v) => [String(v.key), v])
  );
  if (bySidecarKey.size === 0) return vars;
  return vars.map((v) => {
    if (!v.secret || !bySidecarKey.has(String(v.key))) return v;
    const local = bySidecarKey.get(String(v.key));
    // A secret's `options` travel in the sidecar with its value (serialize.js).
    return {
      ...v,
      value: local.value ?? '',
      ...(Array.isArray(local.options) && local.options.length > 0 ? { options: local.options } : {}),
    };
  });
}

/**
 * Restore the mock slot. Missing/invalid YAML yields the normalized empty
 * default (`{ responseExamples: [] }`), the fixed-point counterpart of the
 * serializer's omit-when-empty rule — so `import(serialize(ws)) == ws`.
 */
function normalizeMockField(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { responseExamples: [] };
  }
  return {
    ...raw,
    responseExamples: Array.isArray(raw.responseExamples) ? raw.responseExamples : [],
  };
}

/** Keys `normalizeRequest` maps itself; everything else in the file is passed through untouched. */
const MAPPED_REQUEST_KEYS = new Set([
  'id', 'name', 'method', 'url', 'description', 'headers', 'params', 'cookies', 'body',
  'body_type', 'bodyType', 'auth', 'settings', 'pre_request_script', 'preRequestScript',
  'test_script', 'testScript', 'sort_order', 'folder_id', 'mock',
]);

function normalizeRequest(raw) {
  if (!raw || typeof raw !== 'object') return null;
  // `type` (http / websocket / sse / grpc), `formDataFields`,
  // `rawBodyLanguage`, `scriptDialect`, … — whatever the request carried
  // (serialize.js writes it all, minus a denylist). The old whitelist lost
  // these on every load (git-sync audit 01.10.2026).
  const passthrough = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!MAPPED_REQUEST_KEYS.has(key)) passthrough[key] = value;
  }
  return {
    ...passthrough,
    id: raw.id,
    name: raw.name || 'Request',
    method: raw.method || 'GET',
    url: raw.url ?? '',
    description: raw.description ?? '',
    headers: Array.isArray(raw.headers) ? raw.headers : [],
    params: Array.isArray(raw.params) ? raw.params : [],
    cookies: Array.isArray(raw.cookies) ? raw.cookies : [],
    body: raw.body ?? '',
    bodyType: raw.body_type ?? raw.bodyType ?? 'json',
    body_type: raw.body_type ?? raw.bodyType ?? 'json',
    // Absent = inherit from folder / collection (utils/requestAuth.js).
    ...(raw.auth != null ? { auth: raw.auth } : {}),
    settings: raw.settings ?? {},
    preRequestScript: raw.pre_request_script ?? raw.preRequestScript ?? '',
    testScript: raw.test_script ?? raw.testScript ?? '',
    sort_order: typeof raw.sort_order === 'number' ? raw.sort_order : 0,
    folder_id: raw.folder_id ?? null,
    mock: normalizeMockField(raw.mock),
  };
}

/**
 * Put secret COLLECTION and FOLDER variable values back from the collection's
 * `collection.local.yaml` (review F1). Rows keep every other field; a secret
 * row the sidecar does not know keeps no value (a teammate's clone).
 */
function withEmptySecretValues(rows) {
  if (!Array.isArray(rows)) return rows;
  return rows.map((r) => (r?.secret && r.value === undefined ? { ...r, value: '' } : r));
}

function mergeCollectionSecrets(settings, local) {
  const next = { ...settings };
  if (Array.isArray(next.collection_variables)) {
    next.collection_variables = withEmptySecretValues(next.collection_variables);
  }
  if (Array.isArray(next.folders)) {
    next.folders = next.folders.map((f) =>
      Array.isArray(f?.variables) && f.variables.some((v) => v?.secret)
        ? { ...f, variables: withEmptySecretValues(f.variables) }
        : f
    );
  }
  if (!local || typeof local !== 'object') return next;
  if (Array.isArray(next.collection_variables)) {
    next.collection_variables = mergeSecretValues(next.collection_variables, {
      variables: local.collection_variables,
    });
  }
  const byFolder = new Map(
    (Array.isArray(local.folders) ? local.folders : [])
      .filter((f) => f && f.id != null)
      .map((f) => [String(f.id), f])
  );
  if (Array.isArray(next.folders) && byFolder.size > 0) {
    next.folders = next.folders.map((f) =>
      Array.isArray(f?.variables) && byFolder.has(String(f.id))
        ? { ...f, variables: mergeSecretValues(f.variables, byFolder.get(String(f.id))) }
        : f
    );
  }
  return next;
}

function normalizeCollection(raw, requests, local) {
  if (!raw || typeof raw !== 'object') return null;
  let settings = { ...(raw.settings || {}) };
  if (Array.isArray(raw.folders)) {
    settings.folders = raw.folders;
  }
  settings = mergeCollectionSecrets(settings, local);
  return {
    id: raw.id,
    name: raw.name || 'Collection',
    description: raw.description ?? '',
    sort_order: typeof raw.sort_order === 'number' ? raw.sort_order : 0,
    settings,
    requests: requests || [],
    ...(raw.local_uid ? { local_uid: raw.local_uid } : {}),
    ...(raw.cloud_collection_id != null ? { cloud_collection_id: raw.cloud_collection_id } : {}),
    ...(raw.link_status ? { link_status: raw.link_status } : {}),
    ...(raw.last_synced_at ? { last_synced_at: raw.last_synced_at } : {}),
  };
}

/**
 * Convert parsed YAML file tree into normalized snapshot shape.
 * @param {object} tree - { manifest, globals, workspaceVars, environments, collections, schemas }
 */
export function fileTreeToSnapshot(tree) {
  const manifest = tree.manifest || {};
  const globalsRaw = tree.globals || {};
  const workspaceVarsRaw = tree.workspaceVars || {};
  const schemasRaw = tree.schemas && typeof tree.schemas === 'object' ? tree.schemas : {};

  const snapshot = {
    version: manifest.version ?? SCHEMA_VERSION,
    workspaceName: manifest.name ?? '',
    id: manifest.id,
    collections: [],
    globalVariables: mergeSecretValues(
      (globalsRaw.variables || []).map(normalizeVariable),
      tree.globalsLocal
    ),
    workspaceVariables: mergeSecretValues(
      (workspaceVarsRaw.variables || []).map(normalizeVariable),
      tree.workspaceVarsLocal
    ),
    environments: [],
    selectedEnvironmentId: manifest.selected_environment ?? manifest.selectedEnvironmentId ?? '',
    schemas: {
      folders: Array.isArray(schemasRaw.folders) ? schemasRaw.folders : [],
      items: Array.isArray(schemasRaw.items) ? schemasRaw.items : [],
    },
  };

  const envSidecars = new Map(
    (tree.environmentsLocal || [])
      .filter((doc) => doc?.id != null)
      .map((doc) => [String(doc.id), doc])
  );
  for (const env of tree.environments || []) {
    if (!env?.id) continue;
    snapshot.environments.push({
      id: env.id,
      name: env.name || String(env.id),
      variables: mergeSecretValues(
        (env.variables || []).map(normalizeVariable),
        envSidecars.get(String(env.id))
      ),
      ...(typeof env.sort_order === 'number' ? { sort_order: env.sort_order } : {}),
    });
  }
  snapshot.environments.sort(bySortOrder);

  for (const colEntry of tree.collections || []) {
    const { meta, requests: reqFiles, local } = colEntry;
    const requests = (reqFiles || [])
      .map(normalizeRequest)
      .filter(Boolean)
      .sort(bySortOrder);
    const col = normalizeCollection(meta, requests, local);
    if (col) snapshot.collections.push(col);
  }

  snapshot.collections.sort(bySortOrder);

  return snapshot;
}

/**
 * Parse a single YAML file's text into an object.
 */
export function parseYamlFile(text) {
  return parseYaml(text);
}
